import { electronicFormat, printFormat } from "iban";
import { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";

enum MONTH {
  JANUARY = "01",
  FEBRUARY = "02",
  MARCH = "03",
  APRIL = "04",
  MAY = "05",
  JUNE = "06",
  JULY = "07",
  AUGUST = "08",
  SEPTEMBER = "09",
  OCTOBER = "10",
  NOVEMBER = "11",
  DECEMBER = "12",
}

const MONTH_LABEL = {
  [MONTH.JANUARY]: "Janvier",
  [MONTH.FEBRUARY]: "Février",
  [MONTH.MARCH]: "Mars",
  [MONTH.APRIL]: "Avril",
  [MONTH.MAY]: "Mai",
  [MONTH.JUNE]: "Juin",
  [MONTH.JULY]: "Juillet",
  [MONTH.AUGUST]: "Août",
  [MONTH.SEPTEMBER]: "Septembre",
  [MONTH.OCTOBER]: "Octobre",
  [MONTH.NOVEMBER]: "Novembre",
  [MONTH.DECEMBER]: "Décembre",
};

export const convertDateToMonthAndYear = (date: string): string => {
  const d = new Date(date).toISOString();
  const month = d.slice(5, 7) as MONTH;
  const year = d.slice(0, 4);

  return `${MONTH_LABEL[month]} ${year}`;
};

export const formatNumberToLocale = (number: number, decimals = 0, locale = "fr-FR"): string => {
  return number.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const formatToCurrency = (amount: number): string => `${formatNumberToLocale(amount, 2)}€`;

export const formatToSignedCurrency = (amount: number, isCredited: boolean): string => {
  let sign = "";
  if (amount !== 0) {
    sign = isCredited ? "+" : "-";
  }
  return `${sign}${formatToCurrency(Math.abs(amount))}`;
};

export const formatDateToLocale = (date: string | Date): string => new Date(date).toLocaleDateString("fr-FR");

export const formatTimeToLocale = (date: string | Date): string =>
  new Date(date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

export const getInitials = (text: string): string => {
  const words = text.split(/\s+/).filter(w => w.length > 0);
  if (words.length >= 2 && words[0] && words[1]) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return (text[0] ?? "?").toUpperCase();
};

// Indicatifs des pays couverts par getCountryDisplayName, du plus long au plus court pour un matching sans ambiguïté
const INTERNATIONAL_PREFIXES = ["+351", "+352", "+353", "+31", "+32", "+33", "+34", "+39", "+41", "+43", "+44", "+49"];

/** Découpe par paires, le premier groupe passant à 3 chiffres quand le compte est impair */
const groupDigits = (digits: string): string => {
  const groups: string[] = [];
  let rest = digits;
  if (rest.length % 2 === 1) {
    groups.push(rest.slice(0, 3));
    rest = rest.slice(3);
  }
  for (let i = 0; i < rest.length; i += 2) groups.push(rest.slice(i, i + 2));
  return groups.join(" ");
};

/**
 * Formate un numéro de téléphone pour l'affichage en le découpant en groupes lisibles.
 * - E.164 français : +33612345678 -> "+33 6 12 34 56 78"
 * - National français : 0612345678 -> "06 12 34 56 78"
 * - Amérique du Nord : +13105551234 -> "+1 310 555 1234"
 * - Autre indicatif connu : +32470123456 -> "+32 470 12 34 56"
 * Toute valeur non reconnue est renvoyée telle quelle.
 */
export const formatPhoneNumber = (phone: string): string => {
  const compacted = phone.replace(/[\s.\-()]/g, "");

  const frenchNumber = /^\+33(\d)(\d{8})$/.exec(compacted);
  if (frenchNumber?.[1] && frenchNumber[2]) return `+33 ${frenchNumber[1]} ${groupDigits(frenchNumber[2])}`;

  if (/^0\d{9}$/.test(compacted)) return groupDigits(compacted);

  const northAmericanNumber = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(compacted);
  if (northAmericanNumber) return `+1 ${northAmericanNumber.slice(1).join(" ")}`;

  const prefix = INTERNATIONAL_PREFIXES.find(p => compacted.startsWith(p));
  const nationalNumber = prefix ? compacted.slice(prefix.length) : "";
  if (prefix && /^\d{4,}$/.test(nationalNumber)) return `${prefix} ${groupDigits(nationalNumber)}`;

  return phone;
};

/**
 * Les IBAN sont envoyés et stockés compacts ("FR7630006000011234567890189") et affichés par
 * groupes de quatre ("FR76 3000 6000 0112 3456 7890 189"), plus lisibles et faciles à comparer
 * avec un RIB.
 */
export const normalizeIban = (iban: string): string => electronicFormat(iban);

export const formatIban = (iban: string): string => printFormat(iban, " ");

/**
 * SIRET et numéros de déclaration d'activité (NDA) sont souvent copiés groupés ("123 456 789 00012",
 * "11 75 12345 67") : ils sont validés et envoyés sans séparateurs, et affichés groupés.
 */
export const normalizeIdentifier = (value: string): string => value.replace(/[\s.-]/g, "");

/** Les SIRET étrangers n'ont pas de format commun : ils sont envoyés tels que saisis. */
export const normalizeSiret = (siret: string, country: SUPPORTED_COUNTRY): string =>
  country === SUPPORTED_COUNTRY.FRANCE ? normalizeIdentifier(siret) : siret.trim();

/** SIREN par groupes de trois puis NIC : "12345678900012" -> "123 456 789 00012". Toute autre valeur est renvoyée telle quelle. */
export const formatSiret = (siret: string): string => {
  const groups = /^(\d{3})(\d{3})(\d{3})(\d{5})$/.exec(normalizeIdentifier(siret));
  return groups ? groups.slice(1).join(" ") : siret;
};

/** Région, département, numéro d'ordre, clé : "11751234567" -> "11 75 12345 67". Toute autre valeur est renvoyée telle quelle. */
export const formatActivityRegistrationNumber = (activityRegistrationNumber: string): string => {
  const groups = /^(\d{2})(\d{2})(\d{5})(\d{2})$/.exec(normalizeIdentifier(activityRegistrationNumber));
  return groups ? groups.slice(1).join(" ") : activityRegistrationNumber;
};
